import {Component, useContext} from "react";
import URI from 'urijs';

import {StoreContext} from "@soprisapps/components/stores/StoreLoader";
import JSONWebToken from "@soprisapps/components/utils/JSONWebToken";
import {PageHead} from "@soprisapps/components/components/header/PageHead";
import ErrorPage from "@soprisapps/components/pages/ErrorPage";
import {SchoolBlocksPageWrapper} from "@soprisapps/components/pages/SchoolBlocksPageWrapper";
import {observer} from "mobx-react";
import WatsonApi from "@soprisapps/components/backends/WatsonApi";
import {withLocaleInURI} from "@soprisapps/components/internationalization/i18nURI";
import {getCookieFromString} from "@soprisapps/components/utils/StringUtilities";
import LanguageTool from "@soprisapps/components/components/header/LanguageTool/LanguageTool";
import {HTML5Backend} from "react-dnd-html5-backend";
import dynamic from "next/dynamic";

const DndProvider = dynamic(() => import("react-dnd").then(module => module.DndProvider));
const AdminSidebar = dynamic(() => import("@soprisapps/components/components/admin/sidebar/AdminSidebar"))

function DragAndDropProvider(props) {
    const {userStore} = useContext(StoreContext);
    if (userStore.isEditor) {
        return <DndProvider backend={HTML5Backend}>
            {props.children}
        </DndProvider>
    } else {
        return props.children;
    }
}

class GridPage extends Component {
    static contextType = StoreContext;

    constructor(props, context) {
        super(props);

        const jwt = new JSONWebToken();
        const token = jwt.decode(props.jwt);
        context.userStore.updateUser(token.user);
    }

    render() {
        if (this.props.view === "error") {
            return <ErrorPage statusCode={this.props.statusCode} />
        }

        try {
            return <DragAndDropProvider>
                <PageHead currentFullUrl={this.props.currentFullUrl} currentTitle={this.props.currentOrganization.title} />
                <LanguageTool />
                <SchoolBlocksPageWrapper {...this.props} />
                {this.context.userStore.editor && <AdminSidebar/>}
            </DragAndDropProvider>
        } catch (error) {
            return <ErrorPage />
        }
    }
}

const isServerReq = req => !req.url.startsWith('/_next');
const pathsRequiringPhpLookups = [
    "/cms/user/myCalendar",
    "/cms/user/settings",
    "/cms/admin/organizationSettings",
    "/calendar",
]

export async function getServerSideProps(ctx) {
    const {req, resolvedUrl, locale} = ctx;
    const skipLookupPaths = ['/service-worker.js', '/manifest.webmanifest', '/favicon.ico'];
    const resolveParentPaths = ['/robots.txt', '/sitemap.xml', '/manifest.webmanifest'];
    const currentBaseUrl = req.headers['x-forwarded-proto'] + "://" + req.headers.host.replace(/\/$/, "");

    const returnProps = {
        currentBaseUrl,
        locale,
        userAgent: req.headers['user-agent'],
    }

    const parsedUri = URI(resolvedUrl);
    const isServerSideRequest = isServerReq(req);
    const fullPath = parsedUri.removeQuery("all");
    const resource = fullPath.resource() || "/";
    const path = fullPath.pathname();
    const parsedQuery = parsedUri.search(true);
    const currentFullUrl = currentBaseUrl + resource;

    let token;
    if (getCookieFromString("PHPSESSID", req.headers.cookie)) {
        // this is important so that we don't accidentally present page as logged in if somehow the session doesn't exist
        token = getCookieFromString("token", req.headers.cookie);
    }

    try {
        if (!skipLookupPaths.includes(resource) && typeof req !== "undefined") {
            const promises = [new Promise(async (res, rej) => {
                try {

                    const expand = []

                    if (isServerSideRequest || process.env.NODE_ENV === "development") {
                        expand.push(
                            "title_organization",
                            "title_organization.locations",
                            "title_organization.navigation",
                            "title_organization.categories",
                            "title_organization.media",
                            "title_organization.primary_hostname",
                            "title_organization.ancestor_tree",
                            "title_organization.ancestor_tree.navigation",
                            "title_organization.ancestor_tree.primary_hostname",
                        )
                    }

                    let client = await WatsonApi(token);

                    const response = await client.apis.lookups.lookups_list({
                        url: resolveParentPaths.includes(resource) ? currentBaseUrl : currentFullUrl,
                        expand,
                        gridType: parsedQuery.gridType ? parsedQuery.gridType : undefined,
                    });
                    const resolveObject = {
                        currentOrganization: response.obj,
                        view: response.obj.view,
                        notifications: response.obj.alerts,
                        jwt: response.obj.jwt,
                    }

                    if (response.obj.blocks) {
                        resolveObject.blocks = response.obj.blocks;
                    }
                    if (response.obj.teachers) {
                        resolveObject.teachers = response.obj.teachers;
                    }
                    if (response.obj.category_tree) {
                        resolveObject.categoryTree = response.obj.category_tree;
                    }
                    if (response.obj.staff_collection) {
                        resolveObject.staffCollection = response.obj.staff_collection;
                    }

                    if (isServerSideRequest || process.env.NODE_ENV === "development") {
                        // on client-side transitions we don't need to replace title_organization
                        resolveObject.organization = response.obj.title_organization;
                    }

                    res(resolveObject);
                } catch (e) {
                    rej(e);
                }
            })];

            if (pathsRequiringPhpLookups.includes(resolvedUrl)) {
                promises.push(new Promise(async (res, rej) => {
                    try {
                        const fetchUrl = encodeURI(`${currentBaseUrl}/lookup/?lookup_url=${withLocaleInURI(resource, locale)}`);

                        // TODO: remove conditional after some time has passed and confident folks have re-authenticated
                        let fetchOptions = {};
                        if (token) {
                            fetchOptions.headers = {
                                'Cookie': req.headers.cookie,
                            }
                        }

                        const response = await fetch(fetchUrl, fetchOptions);
                        const data = await response.json();
                        res(data);
                    } catch (e) {
                        rej(e);
                    }
                }))
            }

            const responses = await Promise.all(promises);
            responses.forEach(r => Object.assign(returnProps, r));
        }

        return {
            notFound: false,
            props: {
                ...returnProps,
                view: resolvedUrl === "/_offline" ? "offline" : returnProps.view,
            },
        }

    } catch (error) {
        console.error(error);
        return {
            props: {
                statusCode: error?.status || 404,
                view: "error",
            }
        }
    }
}
export default GridPage;
